import { FC, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import {
  AuthRoutesAccess,
  HomeRouteAccess,
  LegacyQuizRouteAccess,
  PrivacyAccess,
  RedirectAnonymousAccess,
  ResultCatalogRouteAccess,
} from '@app/web/src/routes/AccessWeb';

import { prependBasename } from '@lib/core/service/utils';
import { PAGES } from '@lib/tools/views/urls';
import { lazyImport } from '@lib/tools/views/utils';

const AIRecommendationContainer = lazyImport(
  () => import('@app/web/src/containers/AIRecommendationContainer'),
  'AIRecommendationContainer',
);
const ActionContainer = lazyImport(() => import('@app/web/src/containers/ActionContainer'), 'ActionContainer');
const LeaderboardContainer = lazyImport(
  () => import('@app/web/src/containers/LeaderboardContainer'),
  'LeaderboardContainer',
);
const AuthenticationContainer = lazyImport(
  () => import('@app/web/src/containers/AuthenticationContainer'),
  'AuthenticationContainer',
);
const ResultCatalogContainer = lazyImport(
  () => import('@app/web/src/containers/Catalog/ResultCatalogContainer'),
  'ResultCatalogContainer',
);
const SearchCatalogContainer = lazyImport(
  () => import('@app/web/src/containers/Catalog/SearchCatalogContainer'),
  'SearchCatalogContainer',
);
const CharacterDetailsContainer = lazyImport(
  () => import('@app/web/src/containers/CharacterDetailsContainer'),
  'CharacterDetailsContainer',
);
const ExploreContainer = lazyImport(() => import('@app/web/src/containers/ExploreContainer'), 'ExploreContainer');
const ExploreListContainer = lazyImport(
  () => import('@app/web/src/containers/ExploreListContainer'),
  'ExploreListContainer',
);
const HomeContainer = lazyImport(() => import('@app/web/src/containers/HomeContainer'), 'HomeContainer');
const CookiePolicyContainer = lazyImport(
  () => import('@app/web/src/containers/Legal/CookiePolicyContainer'),
  'CookiePolicyContainer',
);
const GeneralServiceTermsContainer = lazyImport(
  () => import('@app/web/src/containers/Legal/GeneralServiceTermsContainer'),
  'GeneralServiceTermsContainer',
);
const PrivacyPolicyContainer = lazyImport(
  () => import('@app/web/src/containers/Legal/PrivacyPolicyContainer'),
  'PrivacyPolicyContainer',
);
const TermsConditionsContainer = lazyImport(
  () => import('@app/web/src/containers/Legal/TermsConditionsContainer'),
  'TermsConditionsContainer',
);
const ProductPageContainer = lazyImport(
  () => import('@app/web/src/containers/ProductContainer'),
  'ProductPageContainer',
);
const QuizChooseExpertiseContainer = lazyImport(
  () => import('@app/web/src/containers/QuizChooseExpertiseContainer'),
  'QuizChooseExpertiseContainer',
);
const RedirectContainer = lazyImport(() => import('@app/web/src/containers/RedirectContainer'), 'RedirectContainer');
const SuggestionContainer = lazyImport(
  () => import('@app/web/src/containers/SuggestionContainer'),
  'SuggestionContainer',
);
const SurveyGameContainer = lazyImport(
  () => import('@app/web/src/containers/SurveyGameContainer'),
  'SurveyGameContainer',
);
const JournalPlacesContainer = lazyImport(
  () => import('@app/web/src/containers/TasteId/Journal/JournalPlacesContainer'),
  'JournalPlacesContainer',
);
const JournalProductsContainer = lazyImport(
  () => import('@app/web/src/containers/TasteId/Journal/JournalProductsContainer'),
  'JournalProductsContainer',
);
const TasteIdContainer = lazyImport(
  () => import('@app/web/src/containers/TasteId/TasteIdContainer'),
  'TasteIdContainer',
);
const TasteIdLegalPageContainer = lazyImport(
  () => import('@app/web/src/containers/TasteId/TasteIdLegalPageContainer'),
  'TasteIdLegalPageContainer',
);
const TasteIdSettingsPageContainer = lazyImport(
  () => import('@app/web/src/containers/TasteId/TasteIdSettingsPageContainer'),
  'TasteIdSettingsPageContainer',
);
const TasteIdTagsPageContainer = lazyImport(
  () => import('@app/web/src/containers/TasteId/TasteIdTagsPageContainer'),
  'TasteIdTagsPageContainer',
);
const ThirdPartyConsentContainer = lazyImport(
  () => import('@app/web/src/containers/ThirdPartyConsentContainer'),
  'ThirdPartyConsentContainer',
);
const WelcomePrivacyContainer = lazyImport(
  () => import('@app/web/src/containers/WelcomePrivacyContainer'),
  'WelcomePrivacyContainer',
);
const WidgetLauncherContainer = lazyImport(
  () => import('@app/web/src/containers/WidgetLauncher/WidgetLauncherContainer'),
  'WidgetLauncherContainer',
);
const LoginContainer = lazyImport(
  () => import('@lib/tools/shared/auths/views/containers/LoginContainer'),
  'LoginContainer',
);
const ResetPasswordContainer = lazyImport(
  () => import('@lib/tools/shared/auths/views/containers/ResetPasswordContainer'),
  'ResetPasswordContainer',
);
const SetPasswordContainer = lazyImport(
  () => import('@lib/tools/shared/auths/views/containers/SetPasswordContainer'),
  'SetPasswordContainer',
);
const LegalTabsContainer = lazyImport(
  () => import('@lib/tools/shared/legals/views/containers/LegalTabsContainer'),
  'LegalTabsContainer',
);
const AgeAndGenderContainer = lazyImport(
  () => import('@lib/tools/shared/quizzes/views/containers/AgeAndGenderContainer'),
  'AgeAndGenderContainer',
);
const FoodHabitsContainer = lazyImport(
  () => import('@lib/tools/shared/quizzes/views/containers/FoodHabitsContainer'),
  'FoodHabitsContainer',
);
const QuizContainer = lazyImport(
  () => import('@lib/tools/shared/quizzes/views/containers/QuizContainer'),
  'QuizContainer',
);
const FoodPairingContainer = lazyImport(
  () => import('@lib/tools/shared/recipes/views/containers/FoodPairingContainer'),
  'FoodPairingContainer',
);

const Router: FC = () => {
  const RouteGroup = (c, ...paths) => {
    return paths.map(p => <Route key={p} Component={c} path={p} />);
  };

  const retailerBasename = '/:lang/:retailer/loc/:location/p/:product';
  const retailerUrl = url => retailerBasename + url;

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path={retailerBasename}>
          <Route
            key="urlWidgetLauncher"
            Component={WidgetLauncherContainer}
            path={retailerUrl(PAGES.vinhood.widgetLauncher)}
          />
          <Route Component={PrivacyAccess}>
            <Route Component={HomeRouteAccess}>
              <Route key="urlHome" Component={HomeContainer} path={retailerUrl(PAGES.vinhood.home)} />
            </Route>

            <Route
              key="urlAIRecommendation"
              Component={AIRecommendationContainer}
              path={retailerUrl(PAGES.vinhood.aiRecommendation)}
            />

            <Route
              key="urlTasteOrExpertSelection"
              Component={QuizChooseExpertiseContainer}
              path={retailerUrl(PAGES.vinhood.quiz.chooseExpert)}
            />

            <Route
              key="urlAgeAndGender"
              Component={AgeAndGenderContainer}
              path={retailerUrl(PAGES.vinhood.ageAndGender)}
            />

            <Route
              key="urlFoodHabits"
              Component={FoodHabitsContainer}
              path={retailerUrl(PAGES.vinhood.quiz.chooseTaste)}
            />

            {RouteGroup(QuizContainer, retailerUrl(`/quiz/:quizType/`))}

            <Route
              key="urlFoodPairing"
              Component={FoodPairingContainer}
              path={retailerUrl(PAGES.vinhood.recipeSearch)}
            />

            <Route Component={ResultCatalogRouteAccess}>
              <Route
                key="urlResultCatalog"
                Component={ResultCatalogContainer}
                path={retailerUrl(PAGES.vinhood.catalog)}
              />
            </Route>
            <Route key="urlSuggestion" Component={SuggestionContainer} path={retailerUrl(PAGES.vinhood.suggestion)} />

            <Route key="urlSurveyGame" Component={SurveyGameContainer} path={retailerUrl(PAGES.vinhood.surveyGame)} />

            <Route
              key="urlSearchCatalog"
              Component={SearchCatalogContainer}
              path={retailerUrl(PAGES.vinhood.searchCatalog)}
            />

            <Route
              key="urlCharacterDetails"
              Component={CharacterDetailsContainer}
              path={retailerUrl(PAGES.vinhood.characterDetails)}
            />

            <Route key="urlExplore" Component={ExploreContainer} path={retailerUrl(PAGES.vinhood.explore)} />

            <Route key="urlAction" Component={ActionContainer} path={retailerUrl(PAGES.vinhood.action)} />

            <Route
              key="urlLeaderboard"
              Component={LeaderboardContainer}
              path={retailerUrl(PAGES.vinhood.leaderboard)}
            />

            <Route
              key="urlExploreList"
              Component={ExploreListContainer}
              path={retailerUrl(PAGES.vinhood.exploreList)}
            />

            {/* TasteId */}
            <Route key="urlTasteId" Component={TasteIdContainer} path={retailerUrl(PAGES.vinhood.tasteId.tasteId)} />
            <Route
              key="urlTasteIdProductsJournal"
              Component={JournalProductsContainer}
              path={retailerUrl(PAGES.vinhood.tasteId.tasteIdProductsJournal)}
            />
            <Route
              key="urlTasteIdPlacesJournal"
              Component={JournalPlacesContainer}
              path={retailerUrl(PAGES.vinhood.tasteId.tasteIdPlacesJournal)}
            />
            <Route
              key="urlTasteIdLegal"
              Component={TasteIdLegalPageContainer}
              path={retailerUrl(PAGES.vinhood.tasteId.tasteIdLegal)}
            />
            <Route
              key="urlTasteIdTags"
              Component={TasteIdTagsPageContainer}
              path={retailerUrl(PAGES.vinhood.tasteId.tasteIdTags)}
            />
            <Route Component={RedirectAnonymousAccess}>
              <Route
                key="urlTasteIdSettings"
                Component={TasteIdSettingsPageContainer}
                path={retailerUrl(PAGES.vinhood.tasteId.tasteIdSettings)}
              />
            </Route>

            <Route key="urlProduct" Component={ProductPageContainer} path={retailerUrl(PAGES.vinhood.product)} />

            {/* User */}
            <Route Component={AuthRoutesAccess}>
              <Route
                key="urlRegistration"
                Component={AuthenticationContainer}
                path={retailerUrl(PAGES.vinhood.registration)}
              />

              <Route key="urlLogin" Component={LoginContainer} path={retailerUrl(PAGES.vinhood.signIn)} />

              <Route
                key="urlResetPassword"
                Component={ResetPasswordContainer}
                path={retailerUrl(PAGES.vinhood.resetPassword)}
              />

              <Route
                key="urlSetPassword"
                Component={SetPasswordContainer}
                path={retailerUrl(PAGES.vinhood.setPassword)}
              />

              <Route
                key="urlThirdPartyConsent"
                Component={ThirdPartyConsentContainer}
                path={retailerUrl(PAGES.vinhood.thirdParty)}
              />
            </Route>

            <Route
              key="urlTastePathStartPart"
              Component={ThirdPartyConsentContainer}
              path={retailerUrl(PAGES.vinhood.tastePathStartPart)}
            />
          </Route>
          {/* Misc */}
          <Route key="urlLegal" Component={LegalTabsContainer} path={retailerUrl(PAGES.vinhood.legal.tabs)} />
          <Route key="urlRedirect" Component={RedirectContainer} path={retailerUrl(PAGES.vinhood.redirect)} />
          <Route
            key="urlGeneralServiceTermsPart"
            Component={GeneralServiceTermsContainer}
            path={retailerUrl(PAGES.vinhood.legal.generalServiceTerms)}
          />
          <Route
            key="urlPrivacyPolicyPart"
            Component={PrivacyPolicyContainer}
            path={retailerUrl(PAGES.vinhood.legal.privacy)}
          />
          <Route
            key="urlCookiePolicyPart"
            Component={CookiePolicyContainer}
            path={retailerUrl(PAGES.vinhood.legal.cookies)}
          />
          <Route
            key="urlTermsAndConditionsPart"
            Component={TermsConditionsContainer}
            path={retailerUrl(PAGES.vinhood.legal.termsConditions)}
          />
          <Route Component={LegacyQuizRouteAccess}>
            <Route element={<Navigate replace to={prependBasename(PAGES.vinhood.home)} />} path={retailerUrl('/*')} />
          </Route>

          <Route
            key="urlWelcomePrivacy"
            Component={WelcomePrivacyContainer}
            path={retailerUrl(PAGES.vinhood.welcomePrivacy)}
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default Router;
